/**
 * V2 Currency API Service
 * V1 Coin/Token API를 대체
 */

import { apiInterceptor } from '@/networks/api/interceptor'
import type { ICurrency, ICurrencyPrice } from '@/types/currency'

export interface ICurrencyAPIService {
  getCurrencyList(params?: {
    currencyIds?: string[]
    type?: 'native' | 'token'
    parentId?: string
  }): Promise<ICurrency[]>
  getCurrencyById(currencyId: string): Promise<ICurrency>
  getCurrencyPrice(currencyId: string): Promise<ICurrencyPrice>
  getCurrencyPrices(currencyIds: string[]): Promise<ICurrencyPrice[]>
  getTokensByCurrency(parentCurrencyId: string): Promise<ICurrency[]>
}

export class CurrencyAPIService implements ICurrencyAPIService {
  /**
   * Currency 목록 조회 (V2)
   * V1: GET /coin, GET /token
   * V2: GET /currencies/coins (코인), GET /currencies/tokens (토큰)
   */
  async getCurrencyList(params?: {
    currencyIds?: string[]
    type?: 'native' | 'token'
    parentId?: string
  }): Promise<ICurrency[]> {
    // type에 따라 다른 엔드포인트 호출
    let endpoint = '/currencies/coins' // 기본값은 coins
    const queryParams = new URLSearchParams()

    if (params?.type === 'token') {
      endpoint = '/currencies/tokens'
      if (params.parentId) {
        queryParams.append('parentId', params.parentId)
      }
    }

    if (params?.currencyIds?.length) {
      queryParams.append('ids', params.currencyIds.join(','))
    }

    const queryString = queryParams.toString()
    endpoint = queryString ? `${endpoint}?${queryString}` : endpoint

    const response = await apiInterceptor.request(endpoint)
    if (!response.ok) {
      throw new Error(`Failed to fetch currencies: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리: { success: true, data: { coins: [...] } }
    const responseData = data.data || data

    // V2 API는 { coins: [...] } 형태로 반환
    if (responseData.coins && Array.isArray(responseData.coins)) {
      // V2 형식을 우리가 사용할 형식으로 변환
      return responseData.coins.map((currency: any) => ({
        id: currency.id, // V2 API의 ID를 그대로 사용 (ETHEREUM, BITCOIN 등)
        name: currency.name,
        symbol: currency.ticker,
        ticker: currency.ticker, // ticker 필드 명시적으로 추가
        type: 'native', // coins 엔드포인트는 native 타입
        decimals: currency.currencyUnits?.[0]?.decimals || 18,
        iconUrl: currency.iconUrl,
        cgkId: currency.cgkId,
        cmcId: currency.cmcId,
        // V2 추가 필드들
        family: currency.family,
        color: currency.color,
        isTestnet: currency.isTestnet,
        currencyClassName: currency.currencyClassName,
        // Block Explorer URL
        blockExplorerUrl: currency.blockExplorerUrl,
      }))
    }

    // tokens 엔드포인트의 경우
    if (responseData.tokens && Array.isArray(responseData.tokens)) {
      return responseData.tokens.map((token: any) => ({
        id: token.id, // V2 API의 ID를 그대로 사용
        name: token.name,
        symbol: token.ticker,
        ticker: token.ticker, // ticker 필드 명시적으로 추가
        type: 'token',
        decimals: token.currencyUnits?.[0]?.decimals || token.decimals || 18,
        iconUrl: token.iconUrl,
        cgkId: token.cgkId,
        parentCurrencyId: token.parentCurrency?.id,
        contractAddress: token.contractAddress,
        // parentCurrency 전체 정보 포함
        parentCurrency: token.parentCurrency
          ? {
              id: token.parentCurrency.id,
              name: token.parentCurrency.name,
              ticker: token.parentCurrency.ticker,
              iconUrl: token.parentCurrency.iconUrl,
              cgkId: token.parentCurrency.cgkId,
              family: token.parentCurrency.family,
              blockExplorerUrl: token.parentCurrency.blockExplorerUrl,
            }
          : undefined,
      }))
    }

    // 배열을 직접 반환하는 경우
    if (Array.isArray(data)) {
      return data
    }

    return []
  }

  /**
   * 단일 Currency 조회 (V2)
   * V1: GET /coin/:id
   * V2: GET /currencies/coins/:id
   */
  async getCurrencyById(currencyId: string): Promise<ICurrency> {
    const response = await apiInterceptor.request(
      `/currencies/coins/${currencyId}`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch currency: ${response.statusText}`)
    }

    const data = await response.json()
    return data.currency || data
  }

  /**
   * Currency 가격 조회 (V2)
   * V2에는 가격 API가 없으므로 빈 데이터 반환
   */
  async getCurrencyPrice(currencyId: string): Promise<ICurrencyPrice> {
    // V2 API does not support price endpoints, returning empty price data
    return {
      id: currencyId,
      current_price: 0,
      currencyId,
      price: 0,
      priceChange24h: 0,
      priceChangePercentage24h: 0,
      change24h: 0,
    }
  }

  /**
   * 여러 Currency 가격 일괄 조회 (V2)
   * V2에는 가격 API가 없으므로 빈 배열 반환
   */
  async getCurrencyPrices(currencyIds: string[]): Promise<ICurrencyPrice[]> {
    // V2 API does not support price endpoints, returning empty price data
    return currencyIds.map((id) => ({
      id,
      current_price: 0,
      currencyId: id,
      price: 0,
      priceChange24h: 0,
      priceChangePercentage24h: 0,
      change24h: 0,
    }))
  }

  /**
   * 특정 Currency의 토큰 목록 조회 (V2)
   * V1: GET /coin/:coinId/token
   * V2: GET /currencies/:id/tokens
   */
  async getTokensByCurrency(parentCurrencyId: string): Promise<ICurrency[]> {
    const response = await apiInterceptor.request(
      `/currencies/${parentCurrencyId}/tokens`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch tokens: ${response.statusText}`)
    }

    const data = await response.json()
    return data.tokens || data
  }
}

// 싱글톤 인스턴스
export const currencyAPIService = new CurrencyAPIService()
